// Upload & read a file: drop any document on a building — Claude reads it, suggests what kind of file it is, and fills in
// what's missing. Nothing is saved until a PM confirms; the history says "read by Claude from <file>, confirmed".
import { useEffect, useMemo, useRef, useState } from 'react';
import { api, write } from '../../lib/api';
import { uploadFile } from '../../lib/files';
import { supabase } from '../../lib/supabase';
import { useLive } from '../../lib/live';
import { money } from '../../lib/claimCalc';
import { fmtDate, fmtDateTime } from '../../lib/format';
import { DOC_TYPES, type Building, type Contract, type ContractKind } from '../../lib/buildings';
import { Button, Drawer, errText, useToast } from '../kit';
import type { Programme } from '../../lib/programme';
import { ProgrammeEditor, useProgrammes } from './Programme';
import { C3Loader } from '../C3Loader';

type Org = { id: string; name: string; kind: string; short_code?: string | null };
type Found = { key: string; value: string | number | string[]; where: string | null };
type Read = { doc_type: string | null; doc_type_reason: string | null; fields: Found[]; notes: string | null; programme?: Programme | null };
export type ReadCall = { id: string; phase: string; error: string | null; output: Read | null; created_at: string; file_ids: string[]; confirmed_at: string | null; contract_id: string | null };

const READABLE = /^(application\/pdf|image\/(jpeg|png|webp|gif)|application\/vnd\.openxmlformats-officedocument\.spreadsheetml\.sheet|text\/csv)$/i;
const canRead = (f: File) => READABLE.test(f.type) || /\.(xlsx|csv)$/i.test(f.name);
const LABEL: Record<string, string> = {
  pc_date: 'PC date',
  revised_pc_date: 'Revised PC date',
  aliases: 'Other names',
  kind: 'Kind of contract',
  title: 'Contract title',
  contract_no: 'Client ref (budget no.)',
  project_no: "Contractor's ref",
  contractor_name: 'Contractor / consultant',
  architect_name: 'Architect of record',
  employer_name: 'Employer',
  works_name: 'Works name',
  sum_vep: 'Contract sum · VEP',
  deposit_pct: 'Deposit %',
  deposit_recovery_pct: 'Deposit recovered per claim %',
  retention_pct: 'Retention %',
  retention_limit_pct: 'Retention limit %',
  dlp_months: 'Defects liability · months',
  signed_on: 'Signed on',
};
const BUILDING_KEYS = ['pc_date', 'revised_pc_date', 'aliases'];
const KIND_FOR: Record<string, ContractKind> = { contract: 'building', main_contract: 'main', subcontract: 'subcontract', consultant: 'consultant' };

const show = (k: string, v: unknown): string => {
  if (v === null || v === undefined || v === '' || (Array.isArray(v) && !v.length)) return '';
  if (Array.isArray(v)) return v.join(', ');
  if (k === 'sum_vep') return money(Number(v));
  if (/_pct$/.test(k)) return `${v} %`;
  if (/date|signed_on/.test(k)) return fmtDate(String(v));
  return String(v);
};
const findOrg = (name: string, orgs: Org[]) => {
  const n = name.toLowerCase().replace(/[^a-z0-9]/g, '');
  return orgs.find((o) => {
    const on = o.name.toLowerCase().replace(/[^a-z0-9]/g, '');
    return on && (n.includes(on) || on.includes(n) || (o.short_code && n === o.short_code.toLowerCase()));
  });
};

/** The latest Claude read on a building that hasn't been used yet (shown as a banner on the building page). */
export function useLatestRead(buildingId: string) {
  const [call, setCall] = useState<ReadCall | null>(null);
  const load = async () => {
    const { data } = await supabase
      .from('ai_calls')
      .select('id, phase, error, output, created_at, file_ids, confirmed_at, contract_id')
      .eq('building_id', buildingId)
      .eq('purpose', 'building_read')
      .order('created_at', { ascending: false })
      .limit(1);
    setCall(((data ?? [])[0] as ReadCall) ?? null);
  };
  useEffect(() => void load(), [buildingId]); // eslint-disable-line react-hooks/exhaustive-deps
  useLive(['ai_calls'], () => void load(), { filter: `building_id=eq.${buildingId}` });
  useEffect(() => {
    if (call?.phase !== 'running') return;
    const t = setInterval(() => void load(), 4000);
    return () => clearInterval(t);
  }, [call?.phase]); // eslint-disable-line react-hooks/exhaustive-deps
  return { call, reload: load };
}

export function ReadFile({
  open,
  onClose,
  building,
  contracts,
  orgs,
  siteId,
  call,
  reloadCall,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  building: Building;
  contracts: Contract[];
  orgs: Org[];
  siteId: string;
  call: ReadCall | null;
  reloadCall: () => Promise<void>;
  onSaved: () => void;
}) {
  const toast = useToast();
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [over, setOver] = useState(false);
  const [docType, setDocType] = useState<string>('');
  const [target, setTarget] = useState<string>('new');
  const [ticks, setTicks] = useState<Record<string, boolean>>({});
  const [fileName, setFileName] = useState('');
  const [step, setStep] = useState<'upload' | 'type' | 'check'>('upload');
  const progs = useProgrammes(building.id);

  const live = call && !call.confirmed_at ? call : null;
  const out = live?.phase === 'done' ? live.output : null;

  // where the read is at decides the step
  useEffect(() => {
    if (!open) return;
    if (out) setStep((s) => (s === 'upload' ? 'type' : s));
    else setStep('upload');
  }, [open, out]);
  useEffect(() => {
    if (!out) return;
    setDocType(out.doc_type ?? 'other');
    const ref = out.fields.find((f) => f.key === 'contract_no')?.value;
    const match = contracts.find((c) => ref && c.contract_no === String(ref));
    setTarget(live?.contract_id ?? match?.id ?? 'new');
  }, [out]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!live?.file_ids?.[0]) return;
    void supabase
      .from('files')
      .select('original_name')
      .eq('id', live.file_ids[0])
      .maybeSingle()
      .then(({ data }) => setFileName((data as { original_name: string } | null)?.original_name ?? 'the file'));
  }, [live?.file_ids]);

  const toContract = Boolean(DOC_TYPES.find((d) => d.value === docType)?.toContract);
  const contract = toContract && target !== 'new' ? contracts.find((c) => c.id === target) ?? null : null;

  const rows = useMemo(() => {
    if (!out) return [];
    return out.fields
      .filter((f) => BUILDING_KEYS.includes(f.key) || toContract)
      .filter((f) => !(f.key === 'kind' && contract))
      .map((f) => {
        const isB = BUILDING_KEYS.includes(f.key);
        let now: unknown = isB ? (building as unknown as Record<string, unknown>)[f.key] : contract ? (contract as unknown as Record<string, unknown>)[f.key] : null;
        let found: unknown = f.value;
        let org: Org | undefined;
        if (f.key === 'contractor_name' || f.key === 'architect_name') {
          const col = f.key === 'contractor_name' ? 'contractor_org_id' : 'architect_org_id';
          const id = contract ? (contract as unknown as Record<string, string | null>)[col] : null;
          now = id ? orgs.find((o) => o.id === id)?.name ?? '' : '';
          org = findOrg(String(f.value), orgs);
        }
        if (f.key === 'aliases') {
          const have = new Set((building.aliases ?? []).map((a) => a.toLowerCase()));
          found = (f.value as string[]).filter((a) => !have.has(a.toLowerCase()) && a.toLowerCase() !== building.name.toLowerCase());
          if (!(found as string[]).length) return null;
        }
        const nowS = show(f.key, now);
        const foundS = show(f.key, found) + (org ? ` → ${org.name}` : f.key.endsWith('_name') && (f.key === 'contractor_name' || f.key === 'architect_name') ? ' (not matched to a company)' : '');
        if (nowS && nowS === show(f.key, found)) return null; // already the same
        return { ...f, value: found as Found['value'], group: isB ? 'Building' : 'Contract', now: nowS, found: foundS, differs: Boolean(nowS), org, usable: !(f.key === 'contractor_name' || f.key === 'architect_name') || Boolean(org) };
      })
      .filter((r): r is NonNullable<typeof r> => r !== null);
  }, [out, toContract, contract, building, orgs]);

  useEffect(() => {
    // empty fields are ticked; changes to filled fields are not
    setTicks(Object.fromEntries(rows.map((r) => [r.key, !r.differs && r.usable])));
  }, [rows]);

  async function upload(files: FileList | File[]) {
    const f = Array.from(files)[0];
    if (!f) return;
    setBusy('Uploading…');
    try {
      const r = await uploadFile(f, { siteId, linkedTable: 'buildings', linkedId: building.id });
      const fileId = (r.row as { id: string }).id;
      if (!canRead(f)) {
        toast({ tone: 'ok', text: `${f.name} saved on the building. SWI AI reads PDF, Excel (.xlsx), CSV and photos — save Word or MS Project files as PDF to have them read.` });
        onSaved();
        return;
      }
      setBusy('Starting SWI AI…');
      await api('building-read', { building_id: building.id, file_id: fileId });
      await reloadCall();
      onSaved();
    } catch (e) {
      toast({ tone: 'error', text: errText(e) });
    } finally {
      setBusy(null);
    }
  }

  async function save() {
    if (!live || !out) return;
    const batch = crypto.randomUUID();
    const tag = `From ${fileName} (read by SWI AI ${fmtDateTime(live.created_at)}) — confirmed`;
    const used = rows.filter((r) => ticks[r.key]);
    setBusy('Saving…');
    try {
      const bVals: Record<string, unknown> = {};
      for (const r of used.filter((x) => x.group === 'Building')) bVals[r.key] = r.key === 'aliases' ? [...(building.aliases ?? []), ...(r.value as string[])] : r.value;
      if (Object.keys(bVals).length) await write('buildings', 'update', { id: building.id, batch_id: batch, values: { ...bVals, _expect_updated_at: building.updated_at }, summary: tag });

      let contractId: string | null = contract?.id ?? null;
      if (toContract) {
        const cVals: Record<string, unknown> = {};
        for (const r of used.filter((x) => x.group === 'Contract')) {
          if (r.key === 'contractor_name') cVals.contractor_org_id = r.org?.id ?? null;
          else if (r.key === 'architect_name') cVals.architect_org_id = r.org?.id ?? null;
          else cVals[r.key] = r.value;
        }
        if (!contract) {
          const kind = (cVals.kind as ContractKind) || KIND_FOR[docType] || 'building';
          const row = await write<{ id: string }>('contracts', 'insert', {
            batch_id: batch,
            values: {
              site_id: siteId,
              status: 'active',
              title: cVals.title ?? `${building.name} — ${DOC_TYPES.find((d) => d.value === docType)?.label.toLowerCase()}`,
              ...cVals,
              kind,
              has_claims: kind === 'building',
              covers_all: kind === 'main',
              building_ids: [building.id],
            },
            summary: tag,
          });
          contractId = row.id;
        } else if (Object.keys(cVals).length) {
          await write('contracts', 'update', { id: contract.id, batch_id: batch, values: { ...cVals, _expect_updated_at: contract.updated_at }, summary: tag });
        }
      }
      await write('files', 'update', {
        id: live.file_ids[0],
        batch_id: batch,
        values: { doc_type: docType || 'other', ...(contractId ? { linked_table: 'contracts', linked_id: contractId } : {}) },
        summary: `Filed ${fileName} as ${DOC_TYPES.find((d) => d.value === docType)?.label ?? 'document'}`,
      });
      await api('building-read', { ai_call_id: live.id, confirm: true });
      toast({ tone: 'ok', text: used.length ? `${used.length} details saved` : 'File filed' });
      await reloadCall();
      onSaved();
      onClose();
    } catch (e) {
      toast({ tone: 'error', text: errText(e) });
    } finally {
      setBusy(null);
    }
  }

  async function dismiss() {
    if (!live) return;
    try {
      await api('building-read', { ai_call_id: live.id, confirm: true });
      await reloadCall();
      onClose();
    } catch (e) {
      toast({ tone: 'error', text: errText(e) });
    }
  }

  const steps = [
    ['upload', 'Upload'],
    ['read', 'SWI AI reads'],
    ['type', 'What file is it?'],
    ['check', 'Check & save'],
  ] as const;
  const cur = step === 'upload' ? (live?.phase === 'running' ? 1 : 0) : step === 'type' ? 2 : 3;

  return (
    <Drawer open={open} wide={step === 'check' && docType === 'programme'} title={`${building.name} — add information from a file`} onClose={onClose}>
      <ol className="rf-steps" aria-label="Steps">
        {steps.map(([k, label], i) => (
          <li key={k} className={i < cur ? 'done' : i === cur ? 'on' : ''} aria-current={i === cur ? 'step' : undefined}>
            <span className="n">{i < cur ? '✓' : i + 1}</span>
            {label}
          </li>
        ))}
      </ol>

      {step === 'upload' && live?.phase === 'running' ? (
        <div className="rf-reading">
          <span className="scan" aria-hidden="true" />
          <div>
            <b>{fileName || 'Your file'}</b>
            <p className="rf-live c3-inline"><C3Loader size={48} variant="levels" label="SWI AI is reading" /> SWI AI is reading it…</p>
            <p className="muted small">Usually under a minute. You can close this — it carries on, and the result waits on the building.</p>
          </div>
        </div>
      ) : step === 'upload' ? (
        <>
          {live?.phase === 'failed' ? <div className="banner banner-warn">SWI AI could not read the last file: {live.error}</div> : null}
          <button
            type="button"
            className={`rf-drop ${over ? 'over' : ''}`}
            onClick={() => input.current?.click()}
            onDragOver={(e) => (e.preventDefault(), setOver(true))}
            onDragLeave={() => setOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setOver(false);
              void upload(e.dataTransfer.files);
            }}
            disabled={Boolean(busy)}
          >
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
              <path d="M12 16V4M7 9l5-5 5 5" />
              <path d="M4 16v3a1 1 0 001 1h14a1 1 0 001-1v-3" />
            </svg>
            <b>{busy ?? 'Drop a file here, or click to choose'}</b>
            <span className="muted small">Contract, proposal, addendum, agreement, BOQ, programme — PDF, Excel (.xlsx), CSV or photo. SWI AI reads it; you tick what to keep.</span>
          </button>
          <input ref={input} type="file" hidden accept=".pdf,image/*,.doc,.docx,.xls,.xlsx,.csv,.mpp" onChange={(e) => e.target.files && void upload(e.target.files)} />
          <p className="muted small">The file's text is data only — nothing in it can make PM C3 send, approve or delete anything. Every read is logged.</p>
        </>
      ) : null}

      {step === 'type' && out ? (
        <div className="rf-type">
          <div>
            <b>What kind of file is {fileName}?</b>
            {out.doc_type_reason ? <p className="muted small" style={{ margin: '4px 0 0' }}>SWI AI: {out.doc_type_reason}</p> : null}
          </div>
          <div className="rf-kinds" role="radiogroup" aria-label="Kind of file">
            {DOC_TYPES.map((d) => (
              <button key={d.value} type="button" role="radio" aria-checked={docType === d.value} className={`rf-kind ${docType === d.value ? 'on' : ''}`} onClick={() => setDocType(d.value)}>
                {out.doc_type === d.value ? <span className="guess">SWI AI's guess</span> : null}
                <b>{d.label}</b>
                <span>{d.help}</span>
              </button>
            ))}
          </div>
          <label className="field">
            <span className="field-label">Attach to</span>
            {toContract ? (
              <select value={target} onChange={(e) => setTarget(e.target.value)}>
                {docType !== 'addendum' ? <option value="new">A new contract on {building.name}</option> : null}
                {contracts.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.title ?? 'Contract'} {c.contract_no ? `· ${c.contract_no}` : ''}
                  </option>
                ))}
              </select>
            ) : (
              <span className="muted small">{building.name} — documents (kept in its close-out download)</span>
            )}
          </label>
          <div className="resp-row">
            <Button onClick={() => void dismiss()}>Not needed</Button>
            <span className="grow" />
            <Button kind="primary" disabled={docType === 'addendum' && target === 'new'} onClick={() => setStep('check')}>
              Next — check what was found
            </Button>
          </div>
        </div>
      ) : null}

      {step === 'check' && out && docType === 'programme' && live ? (
        <ProgrammeEditor
          building={building}
          file={{ id: live.file_ids[0], original_name: fileName }}
          initial={out.programme ?? null}
          all={progs.files}
          granted={progs.granted}
          aiCallId={live.id}
          claudeNotes={out.notes}
          onCancel={() => setStep('type')}
          onDone={() => {
            void reloadCall();
            onSaved();
            onClose();
          }}
        />
      ) : null}

      {step === 'check' && out && docType !== 'programme' ? (
        <div className="rf-check">
          <div className="resp-row">
            <b className="grow">Found in the file — tick what to keep</b>
            <span className="muted small">
              {Object.values(ticks).filter(Boolean).length} of {rows.length} ticked
            </span>
          </div>
          {rows.length ? (
            <div className="table-wrap">
              <table className="t">
                <thead>
                  <tr>
                    <th />
                    <th>Field</th>
                    <th>Now in PM C3</th>
                    <th>Found by SWI AI</th>
                    <th>Where</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.key}>
                      <td>
                        <input type="checkbox" aria-label={`Keep ${LABEL[r.key] ?? r.key}`} checked={Boolean(ticks[r.key])} disabled={!r.usable} onChange={(e) => setTicks({ ...ticks, [r.key]: e.target.checked })} />
                      </td>
                      <td>
                        <b>{LABEL[r.key] ?? r.key}</b>
                        <div className="muted small">{r.group}</div>
                      </td>
                      <td className={r.now ? '' : 'muted'}>{r.now || 'empty'}</td>
                      <td className={r.differs ? 'warn-t' : ''}>{r.found}</td>
                      <td className="small" style={{ color: '#6A3D8F', fontWeight: 700 }}>
                        {r.where ?? ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="muted">Nothing new found — the file will just be filed.</p>
          )}
          {out.notes ? <div className="banner banner-info">SWI AI's notes: {out.notes}</div> : null}
          <div className="resp-row">
            <span className="muted small grow">
              {toContract ? (contract ? `Saves to ${building.name} and contract ${contract.contract_no ?? contract.title}` : `Creates a new contract on ${building.name}`) : `Saves to ${building.name}`}
            </span>
            <Button onClick={() => setStep('type')}>Back</Button>
            <Button kind="primary" disabled={Boolean(busy)} onClick={() => void save()}>
              {busy ?? `Save ${Object.values(ticks).filter(Boolean).length} changes`}
            </Button>
          </div>
        </div>
      ) : null}
    </Drawer>
  );
}
